<template>
  <div>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark> mdi-arrow-left </v-icon>
              </v-btn>
              <h3 class="font-weight-bold">KYC Documents - {{ userDetails.first_name }} {{ userDetails.last_name }}</h3>
            </v-card-title>
            <v-card-text>
              <!-- User Info Section -->
              <v-row>
                <v-col md="6">
                  <v-card outlined class="pa-3">
                    <div class="mb-2"><strong>Name:</strong> {{ userDetails.first_name }} {{ userDetails.middle_name }} {{ userDetails.last_name }}</div>
                    <div class="mb-2"><strong>Email:</strong> {{ userDetails.email }}</div>
                    <div class="mb-2"><strong>Phone:</strong> {{ userDetails.phone }}</div>
                  </v-card>
                </v-col>
                <v-col md="6">
                  <v-card outlined class="pa-3">
                    <div class="mb-2"><strong>User Name:</strong> {{ userDetails.user_name }}</div>
                    <div class="mb-2">
                      <strong>Status:</strong>
                      <v-chip small :color="userDetails.is_active ? 'green' : 'red'" text-color="white" class="ml-2">
                        {{ userDetails.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </div>
                    <div><strong>Wallet:</strong> {{ userDetails.wallet || '0' }}</div>
                  </v-card>
                </v-col>
              </v-row>
              
              <!-- Filters Section -->
              <v-row class="mt-4">
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search documents..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), fetchKycDocuments()"
                    @keydown.enter="fetchKycDocuments()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    clearable
                    @click:clear="(filter.status = null), fetchKycDocuments()"
                    label="Document Status"
                    @input="fetchKycDocuments"
                    prepend-inner-icon="mdi-clipboard-check"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="activeStatuses"
                    clearable
                    @click:clear="(filter.is_active = null), fetchKycDocuments()"
                    label="Active Status"
                    @input="fetchKycDocuments"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <!-- Loading Section -->
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>

              <!-- KYC Documents Table -->
              <v-simple-table
                v-else
                fixed-header
                style="min-height: 300px"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Document Name</th>
                      <th class="text-left">Document Number</th>
                      <th class="text-left">Document Type</th>
                      <th class="text-left">Verified At</th>
                      <th class="text-left">Remarks</th>
                      <th class="text-right action_th">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="kycDocuments.length != 0">
                    <tr
                      v-for="(document, i) in kycDocuments"
                      :key="`kyc_doc_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': kycDocuments.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td>
                        <v-tooltip bottom v-if="document.status == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="orange"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-clock-outline</v-icon>
                              </v-avatar>
                              Pending
                            </v-chip>
                          </template>
                          <span>Document is Pending</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="document.status == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="green"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-check-circle</v-icon>
                              </v-avatar>
                              Approved
                            </v-chip>
                          </template>
                          <span>Document is Approved</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="document.status == 2">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Rejected
                            </v-chip>
                          </template>
                          <span>Document is Rejected</span>
                        </v-tooltip>
                      </td>
                      <td>{{ document.doc_name || 'N/A' }}</td>
                      <td>{{ document.doc_number || 'N/A' }}</td>
                      <td>{{ document.doc_type?.name || 'N/A' }}</td>
                      <td>{{ formatDate(document.verified_at) }}</td>
                      <td>
                        <span v-if="document.remarks" class="text-truncate" style="max-width: 150px; display: inline-block;">
                          {{ document.remarks }}
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td class="text-right">
                        <!-- View Document Button -->
                        <v-tooltip bottom v-if="document.doc_path">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-1"
                              color="info"
                              @click="viewDocument(document.doc_path, document.doc_name)"
                            >
                              <v-icon dark size="12">mdi-file-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View Document</span>
                        </v-tooltip>

                        <!-- Approve Button (only for pending) -->
                        <v-tooltip bottom v-if="document.status === 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-1"
                              color="success"
                              @click="openApproveDialog(document)"
                              :loading="approvingId === document.id"
                            >
                              <v-icon dark size="12">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve Document</span>
                        </v-tooltip>

                        <!-- Reject Button (only for pending) -->
                        <v-tooltip bottom v-if="document.status === 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-1"
                              color="error"
                              @click="openRejectDialog(document)"
                              :loading="rejectingId === document.id"
                            >
                              <v-icon dark size="12">mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject Document</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="8" class="text-center">No KYC documents found for this user</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Document Viewer Dialog -->
      <v-dialog v-model="documentDialog" max-width="900">
        <v-card rounded="xxl">
          <v-card-title class="headline">{{ documentTitle }}</v-card-title>
          <v-card-text class="text-center pa-0">
            <img
              v-if="currentDocumentUrl && isImageFile(currentDocumentUrl)"
              :src="currentDocumentUrl"
              style="max-width: 100%; max-height: 600px"
              alt="KYC Document"
            />
            <iframe
              v-else-if="currentDocumentUrl && isPdfFile(currentDocumentUrl)"
              :src="currentDocumentUrl"
              style="width: 100%; height: 600px"
              frameborder="0"
            ></iframe>
            <div v-else class="pa-4">
              <v-icon large color="grey">mdi-file-document</v-icon>
              <p class="mt-2">Document preview not available</p>
              <v-btn color="primary" :href="currentDocumentUrl" target="_blank">
                <v-icon left>mdi-download</v-icon>
                Download Document
              </v-btn>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="documentDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Approve Confirmation Dialog -->
      <v-dialog v-model="approveDialog" width="500">
        <v-card rounded="xxl">
          <v-card-title class="headline">Approve KYC Document</v-card-title>
          <v-card-text>
            Are you sure you want to approve the KYC document "{{ selectedDocument?.doc_name }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="approveDialog = false">Cancel</v-btn>
            <v-btn
              color="success"
              text
              @click="approveDocument"
              :loading="approvingId !== null"
            >
              Approve
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Dialog with Remark Input -->
      <v-dialog v-model="rejectDialog" width="600">
        <v-card rounded="xxl">
          <v-card-title class="headline">Reject KYC Document</v-card-title>
          <v-card-text>
            <div class="mb-4">
              You are about to reject the KYC document "{{ selectedDocument?.doc_name }}".
            </div>
            <v-textarea
              v-model="rejectRemark"
              outlined
              label="Rejection Remark (Required)"
              placeholder="Please provide a reason for rejection..."
              :error-messages="remarkError"
              rows="4"
              counter="500"
              maxlength="500"
              required
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeRejectDialog">Cancel</v-btn>
            <v-btn
              color="error"
              text
              @click="rejectDocument"
              :loading="rejectingId !== null"
              :disabled="!rejectRemark || rejectRemark.trim().length === 0"
            >
              Reject
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "UserKycDocumentsPage",
  data() {
    return {
      userId: null,
      userDetails: {},
      kycDocuments: [],
      filter: {
        search_keyword: "",
        status: "",
        is_active: "",
      },
      statusOptions: [
        { text: "All Statuses", value: "" },
        { text: "Pending", value: "0" },
        { text: "Approved", value: "1" },
        { text: "Rejected", value: "2" },
      ],
      activeStatuses: [
        { text: "All Active Status", value: "" },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      isLoading: false,
      documentDialog: false,
      currentDocumentUrl: null,
      documentTitle: "",
      approveDialog: false,
      rejectDialog: false,
      selectedDocument: null,
      rejectRemark: "",
      remarkError: "",
      approvingId: null,
      rejectingId: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    };
  },
  mounted() {
    this.userId = this.$route.params.userId;
    this.fetchUserDetails();
    this.fetchKycDocuments();
  },
  methods: {
    async fetchUserDetails() {
      try {
        const response = await axios.get(`/users/${this.userId}`);
        this.userDetails = response.data.data;
      } catch (error) {
        console.error("Error fetching user details:", error);
        this.showSnackbar('Failed to load user details', 'error');
      }
    },
    
    async fetchKycDocuments() {
      try {
        this.isLoading = true;
        
        let query = new URLSearchParams({
          user_id: this.decryptIt(this.userId),
          ...this.filter,
        });

        const response = await axios.get(`/user_kyc_documents?${query}`);
        this.kycDocuments = response.data.data;
      } catch (error) {
        console.error("Error fetching KYC documents:", error);
        this.showSnackbar('Failed to load KYC documents', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    viewDocument(docPath, docName) {
      const mediaUrl = 'https://blr1.vultrobjects.com/space-1/';
      this.currentDocumentUrl = `${mediaUrl}${docPath}`;
      this.documentTitle = docName || 'KYC Document';
      this.documentDialog = true;
    },

    isImageFile(url) {
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
    },

    isPdfFile(url) {
      return /\.pdf$/i.test(url);
    },

    openApproveDialog(document) {
      this.selectedDocument = document;
      this.approveDialog = true;
    },

    openRejectDialog(document) {
      this.selectedDocument = document;
      this.rejectRemark = "";
      this.remarkError = "";
      this.rejectDialog = true;
    },

    closeRejectDialog() {
      this.rejectDialog = false;
      this.selectedDocument = null;
      this.rejectRemark = "";
      this.remarkError = "";
    },

    async approveDocument() {
      if (!this.selectedDocument) return;

      this.approvingId = this.selectedDocument.id;
      try {
        await axios.patch(`/user_kyc_documents/${this.encryptIt(this.selectedDocument.id)}`, {
          status: 1,
        });

        this.approveDialog = false;
        this.selectedDocument = null;
        this.showSnackbar('KYC document approved successfully!', 'success');
        this.fetchKycDocuments(); // Refresh the list
      } catch (error) {
        console.error("Error approving document:", error);
        if (error.response && error.response.data) {
          const message = error.response.data.message || 'Failed to approve document';
          this.showSnackbar(message, 'error');
        } else {
          this.showSnackbar('Failed to approve document. Please try again.', 'error');
        }
      } finally {
        this.approvingId = null;
      }
    },

    async rejectDocument() {
      if (!this.selectedDocument || !this.rejectRemark.trim()) {
        this.remarkError = "Rejection remark is required";
        return;
      }

      this.rejectingId = this.selectedDocument.id;
      try {
        await axios.patch(`/user_kyc_documents/${this.encryptIt(this.selectedDocument.id)}`, {
          status: 2,
          remarks: this.rejectRemark.trim(),
        });

        this.closeRejectDialog();
        this.showSnackbar('KYC document rejected successfully!', 'success');
        this.fetchKycDocuments(); // Refresh the list
      } catch (error) {
        console.error("Error rejecting document:", error);
        if (error.response && error.response.data) {
          const message = error.response.data.message || 'Failed to reject document';
          this.showSnackbar(message, 'error');
        } else {
          this.showSnackbar('Failed to reject document. Please try again.', 'error');
        }
      } finally {
        this.rejectingId = null;
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>